'use client';

import dynamic from 'next/dynamic';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import ShareIcon from '@upzi/web-shell/icons/share-icon';
import { Button } from '@upzi/web-shell/ui/button';
import { cn } from '@upzi/web-shell/cn';
import type { JobSharePopover as SharePopover } from './job-share-popover';

function loadSharePopover() {
  return import('./job-share-popover');
}

const JobSharePopover = dynamic(
  () => loadSharePopover().then(module => module.JobSharePopover),
  { ssr: false }
);

export function LazyJobSharePopover({
  isPreviewAction = false,
  triggerClassName,
  ...props
}: Omit<ComponentProps<typeof SharePopover>, 'initialOpen'>) {
  const [isActivated, setIsActivated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  const preload = () => {
    void loadSharePopover().catch(() => {
      // A failed preload can be retried when the user opens the popover.
    });
  };

  if (isActivated) {
    return (
      <JobSharePopover
        {...props}
        initialOpen
        isPreviewAction={isPreviewAction}
        triggerClassName={triggerClassName}
      />
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="icon-lg"
      aria-label="Chia sẻ việc làm"
      aria-haspopup="dialog"
      aria-expanded={false}
      aria-busy={isLoading}
      disabled={isLoading}
      className={cn(
        'border-outline bg-surface-pure text-on-surface hover:bg-primary-variant shrink-0 rounded-full shadow-none',
        isPreviewAction && 'border-base-border size-10 rounded-[14px]',
        triggerClassName
      )}
      onFocus={preload}
      onPointerEnter={preload}
      onClick={async () => {
        setIsLoading(true);

        try {
          await loadSharePopover();
          if (isMounted.current) setIsActivated(true);
        } catch (error) {
          console.error(
            '[JobSharePopover] Unable to load sharing options',
            error
          );
        } finally {
          if (isMounted.current) setIsLoading(false);
        }
      }}
    >
      <ShareIcon size={16} aria-hidden />
    </Button>
  );
}
